import { ErrorHandler, inject, Injectable, Injector } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { AppUpdatedModal } from "../modules/shared/modals/app-updated-modal/app-updated.modal";

// A deploy replaces every hashed chunk, so a session loaded from the previous build
// fails to lazy-load anything it hadn't already fetched.
export function isChunkLoadError(error: unknown): boolean {
    if (!error) {
        return false;
    }

    const err = error as { name?: string; message?: string };
    if (err.name === "ChunkLoadError") {
        return true;
    }

    return /Loading chunk \S+ failed|(?:error loading|Failed to fetch) dynamically imported module/i.test(
        err.message ?? String(error)
    );
}

@Injectable({ providedIn: "root" })
export class AppUpdateService {
    private _dialog = inject(MatDialog);
    private _promptOpen = false;

    promptReload(): void {
        if (this._promptOpen) {
            return;
        }

        this._promptOpen = true;
        this._dialog
            .open(AppUpdatedModal, {
                disableClose: true,
                width: "600px",
                panelClass: "dialog-warning"
            })
            .afterClosed()
            .subscribe((reload) => {
                this._promptOpen = false;
                if (reload) {
                    window.location.reload();
                }
            });
    }
}

// Catches chunk failures outside of routing; router navigations go through
// withNavigationErrorHandler in main.ts.
@Injectable()
export class AppErrorHandler extends ErrorHandler {
    // Resolved lazily — AppUpdateService pulls in MatDialog, which isn't available
    // this early in bootstrap.
    private _injector = inject(Injector);

    override handleError(error: unknown): void {
        if (isChunkLoadError(error)) {
            this._injector.get(AppUpdateService).promptReload();
        }

        super.handleError(error);
    }
}
